import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  TextField, 
  Switch, 
  Button,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import ServiceCatalogForm from '../components/ServiceCatalogForm';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const ServiceCatalogPage = () => {
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [isServiceCatalogLoading, setIsServiceCatalogLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  const apiBase = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  const tenantId = 1; // Replace with context/config if needed
  const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';

  useEffect(() => {
    fetchServiceCatalog();
  }, [includeInactive]);

  const fetchServiceCatalog = async () => {
    setIsServiceCatalogLoading(true);
    try {
      const params = new URLSearchParams({
        tenantId: tenantId,
        cityCode: cityCode,
        includeInactive: includeInactive
      });
      
      const response = await fetch(`${apiBase}/api/service-catalog?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch service catalog');
      }
      setServiceCatalog(data);
    } catch (error) {
      showSnackbar('Failed to fetch service catalog', 'error');
    } finally {
      setIsServiceCatalogLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateService = () => {
    setSelectedService(null);
    setFormError(null);
    setShowForm(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setFormError(null);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    setFormError(null);
    
    try {
      const isEditing = !!selectedService;
      const url = isEditing 
        ? `${apiBase}/api/service-catalog/${selectedService.id}`
        : `${apiBase}/api/service-catalog`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${isEditing ? 'update' : 'create'} service`);
      }

      showSnackbar(data.message || `Service ${isEditing ? 'updated' : 'created'} successfully`);
      setShowForm(false);
      fetchServiceCatalog();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteService = async (service) => {
    setServiceToDelete(service);
    setDeleteWarning(null);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (forceDelete = false) => {
    if (!serviceToDelete) return;

    try {
      const params = forceDelete ? '?force=true' : '';
      const response = await fetch(`${apiBase}/api/service-catalog/${serviceToDelete.id}${params}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400 && data.bookingCount > 0) {
          setDeleteWarning({
            title: 'Dependencies Found',
            message: `This service is used in ${data.bookingCount} booking(s).`,
            details: data.suggestion
          });
          return; // Keep dialog open to show warning
        }
        throw new Error(data.error || 'Failed to delete service');
      }

      showSnackbar(data.message || 'Service deleted successfully');
      setShowDeleteDialog(false);
      setServiceToDelete(null);
      setDeleteWarning(null);
      fetchServiceCatalog();
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleServiceActiveToggle = async (serviceId, newActive) => {
    try {
      const response = await fetch(`${apiBase}/api/service-catalog/${serviceId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: newActive })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update service status');
      }
      fetchServiceCatalog();
    } catch (error) {
      // Optionally handle error
    }
  };


  // Filter and search logic
  const filteredServices = serviceCatalog.filter(service => {
    const matchesSearch = !searchTerm || 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.priceModel.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !typeFilter || service.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  const serviceTypes = [...new Set(serviceCatalog.map(service => service.type))];

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
  };

  const serviceColumns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value.replace('_', ' ')} 
          size="small" 
          variant="outlined" 
        />
      )
    },
    { field: 'priceModel', headerName: 'Price Model', width: 150 },
    { 
      field: 'rate', 
      headerName: 'Rate', 
      width: 100,
      renderCell: (params) => `$${params.value.toFixed(2)}`
    },
    { field: 'unit', headerName: 'Unit', width: 100 },
    { 
      field: 'seasonStart', 
      headerName: 'Season Start', 
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    { 
      field: 'seasonEnd', 
      headerName: 'Season End', 
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    { 
      field: 'checklist', 
      headerName: 'Tasks', 
      width: 120,
      renderCell: (params) => {
        const checklist = params.row.checklist || [];
        const taskCount = checklist.length;
        const totalMinutes = checklist.reduce((sum, task) => sum + (task.estimatedMinutes || 0), 0);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {taskCount} tasks
            </Typography>
            {totalMinutes > 0 && (
              <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                ~{totalMinutes}m
              </Typography>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'active', 
      headerName: 'Active', 
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.row.active}
          onChange={(e) => handleServiceActiveToggle(params.row.id, e.target.checked)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditService(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteService(params.row)}
          sx={{ color: 'error.main' }}
        />
      ]
    }
  ];

  return (
    <AdminPageLayout
      title="Service Catalog Management"
      subtitle="Manage your cleaning services, pricing, and availability"
      maxWidth="xl"
    >
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Include Inactive"
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchServiceCatalog} disabled={isServiceCatalogLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateService}
          >
            Add Service
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Filter by Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {serviceTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(searchTerm || typeFilter) && (
              <Button
                size="small"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
              >
                Clear Filters
              </Button>
            )}

            <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
              {filteredServices.length} of {serviceCatalog.length} services
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ height: 600, width: '100%' }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <DataGrid
              rows={filteredServices}
              columns={serviceColumns}
              getRowId={(row) => row.id}
              loading={isServiceCatalogLoading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            />
          </Paper>
        </Box>

        {/* Service Form Dialog */}
        <ServiceCatalogForm
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          service={selectedService}
          tenantId={tenantId}
          cityCode={cityCode}
          loading={formLoading}
          error={formError}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setServiceToDelete(null);
            setDeleteWarning(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Service"
          message="Are you sure you want to delete this service?"
          itemName={serviceToDelete?.name}
          warning={deleteWarning}
          allowForceDelete={!!deleteWarning}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </AdminPageLayout>
  );
};

export default ServiceCatalogPage; 